
import React, { useState } from 'react';
import { useArticles } from '../contexts/ArticleContext';
import { Link } from 'react-router-dom';
import './Archive.css';

const Archive = () => {
  const { getArticlesByYear, getAllArticles } = useArticles();
  const [selectedYear, setSelectedYear] = useState('2023');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const articlesByYear = getArticlesByYear();
  const allArticles = getAllArticles();
  
  const years = Object.keys(articlesByYear).sort((a, b) => b - a);

  // 统计分类
  const categories = {};
  allArticles.forEach(article => {
    const category = article.category || '未分类';
    if (!categories[category]) {
      categories[category] = 0;
    }
    categories[category]++;
  });

  // 处理分类点击
  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  // 处理年份点击
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedCategory('all'); // 点击年份时重置分类
  };

  // 获取当前显示的文章
  const getCurrentArticles = () => {
    let filteredArticles = [];
    
    // 按年份筛选
    if (selectedYear !== 'all' && articlesByYear[selectedYear]) {
      filteredArticles = [...articlesByYear[selectedYear]];
    } else {
      // 显示所有年份的文章
      Object.values(articlesByYear).forEach(yearArticles => {
        filteredArticles.push(...yearArticles);
      });
    }
    
    // 按分类筛选
    if (selectedCategory !== 'all') {
      filteredArticles = filteredArticles.filter(
        article => article.category === selectedCategory
      );
    }
    
    return filteredArticles;
  };

  const currentArticles = getCurrentArticles();
  
  // 为分类列表添加"全部"选项
  const categoryList = [
    { name: 'all', displayName: '全部', count: allArticles.length },
    ...Object.entries(categories).map(([name, count]) => ({ 
      name, 
      displayName: name,
      count 
    }))
  ];

  // 为年份列表添加"全部"选项
  const yearList = [
    { year: 'all', displayName: '全部', count: allArticles.length },
    ...years.map(year => ({ 
      year, 
      displayName: `${year}年`,
      count: articlesByYear[year].length
    }))
  ];

  return (
    <div className="archive">
      <div className="archive-header">
        <h1>文章归档</h1>
        <p>记录每一段代码的旅程，汇聚每一份技术的思考</p>
      </div>
      
      <div className="archive-content">
        <div className="archive-sidebar">
          <div className="years-section">
            <h3>按年份</h3>
            <div className="years-list">
              {yearList.map(item => (
                <button
                  key={item.year}
                  className={`year-btn ${selectedYear === item.year ? 'active' : ''}`}
                  onClick={() => handleYearClick(item.year)}
                >
                  {item.displayName} ({item.count})
                </button>
              ))}
            </div>
          </div>
          
          <div className="categories-section">
            <h3>分类</h3>
            <div className="categories-list">
              {categoryList.map(category => (
                <button
                  key={category.name}
                  className={`category-btn ${selectedCategory === category.name ? 'active' : ''}`}
                  onClick={() => handleCategoryClick(category.name)}
                >
                  <span className="category-name">{category.displayName}</span>
                  <span className="category-count">{category.count}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="archive-stats">
            <h3>统计</h3>
            <div className="stats-grid">
              <div className="stat-box">
                <div className="stat-number">{allArticles.length}</div>
                <div className="stat-label">文章总数</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{years.length}</div>
                <div className="stat-label">写作年份</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{Object.keys(categories).length}</div>
                <div className="stat-label">分类数量</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="archive-main">
          <div className="archive-main-header">
            <h2 className="archive-title">
              {selectedYear !== 'all' ? `${selectedYear}年` : '所有年份'}
              {selectedCategory !== 'all' ? ` - ${selectedCategory}分类` : ''}
              <span className="article-count">（{currentArticles.length} 篇）</span>
            </h2>
            <div className="active-filters">
              {selectedYear !== 'all' && (
                <span className="active-filter-tag" onClick={() => handleYearClick('all')}>
                  {selectedYear}年 ×
                </span>
              )}
              {selectedCategory !== 'all' && (
                <span className="active-filter-tag" onClick={() => handleCategoryClick('all')}>
                  {selectedCategory} ×
                </span>
              )}
              {(selectedYear !== 'all' || selectedCategory !== 'all') && (
                <button 
                  className="clear-filters-btn"
                  onClick={() => {
                    setSelectedYear('2023');
                    setSelectedCategory('all');
                  }}
                >
                  清除筛选
                </button>
              )}
            </div>
          </div>
          
          {currentArticles.length > 0 ? (
            <div className="articles-timeline">
              {currentArticles.map(article => (
                <div key={article.id} className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-date">{article.date}</div>
                    <h3 className="timeline-title">
                      <Link to={`/article/${article.id}`}>{article.title}</Link>
                    </h3>
                    <p className="timeline-excerpt">{article.excerpt}</p>
                    <div className="timeline-meta">
                      <span className="timeline-category">{article.category}</span>
                      <div className="timeline-actions">
                        <span className="timeline-author">作者：{article.author}</span>
                        <Link to={`/article/${article.id}`} className="timeline-read">
                          阅读全文 →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-articles">
              <p>暂无文章</p>
              <p className="no-articles-hint">
                尝试选择其他年份或分类查看文章
              </p>
              <button 
                className="reset-filters-btn"
                onClick={() => {
                  setSelectedYear('2023');
                  setSelectedCategory('all');
                }}
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Archive;